import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { 
  ClipboardList, 
  FileText, 
  Users,
  ArrowRight,
  CheckCircle,
  Menu,
  X,
  Clock,
  Zap,
  BarChart3,
  ShieldCheck,
  Info
} from 'lucide-react';
import { useState, useEffect } from 'react';
import { Button } from '../../components/ui/button';

function MarketingNav() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { user } = useAuth();

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-md border-b border-slate-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <Link to="/" className="flex items-center">
            <img src="/logo.png" alt="Reliefify" className="h-10 w-auto object-contain" />
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-8">
            <a href="#features" className="text-slate-600 hover:text-slate-900 transition-colors">Features</a>
            <a href="#pricing" className="text-slate-600 hover:text-slate-900 transition-colors">Pricing</a>
            <a href="#about" className="text-slate-600 hover:text-slate-900 transition-colors">About</a>
          </div>

          <div className="hidden md:flex items-center gap-3">
            {user ? (
              <Link to="/app">
                <Button className="h-11 px-6 rounded-full bg-teal-600 hover:bg-teal-700 text-white font-semibold">
                  Go to App
                </Button>
              </Link>
            ) : (
              <>
                <Link to="/login">
                  <Button variant="ghost" className="h-11 px-6 rounded-full font-medium">Login</Button>
                </Link>
                <Link to="/signup">
                  <Button className="h-11 px-6 rounded-full bg-teal-600 hover:bg-teal-700 text-white font-semibold">
                    Start Free Trial
                  </Button>
                </Link>
              </>
            )}
          </div>

          {/* Mobile menu button */}
          <button 
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden p-2 rounded-lg hover:bg-slate-100"
            data-testid="mobile-menu-toggle"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-white border-t border-slate-100 px-4 py-4 space-y-3">
          <a href="#features" className="block py-2 text-slate-600" onClick={() => setMobileMenuOpen(false)}>Features</a>
          <a href="#pricing" className="block py-2 text-slate-600" onClick={() => setMobileMenuOpen(false)}>Pricing</a>
          <a href="#about" className="block py-2 text-slate-600" onClick={() => setMobileMenuOpen(false)}>About</a>
          <hr className="my-3" />
          {user ? (
            <Link to="/app" onClick={() => setMobileMenuOpen(false)}>
              <Button className="w-full h-12 rounded-full bg-teal-600 hover:bg-teal-700 text-white font-semibold">
                Go to App
              </Button>
            </Link>
          ) : (
            <>
              <Link to="/login" onClick={() => setMobileMenuOpen(false)}>
                <Button variant="outline" className="w-full h-12 rounded-full font-medium mb-2">Login</Button>
              </Link>
              <Link to="/signup" onClick={() => setMobileMenuOpen(false)}>
                <Button className="w-full h-12 rounded-full bg-teal-600 hover:bg-teal-700 text-white font-semibold">
                  Start Free Trial
                </Button>
              </Link>
            </>
          )}
        </div>
      )}
    </nav>
  );
}

function HeroSection() {
  return (
    <section className="pt-28 pb-20 md:pt-40 md:pb-32 bg-white">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto text-center">
          {/* Status Badge */}
          <div className="inline-flex items-center gap-1.5 px-3 py-1 mb-6 bg-teal-50 border border-teal-100 rounded-full">
            <div className="w-1.5 h-1.5 bg-teal-500 rounded-full"></div>
            <span className="text-teal-700 text-sm font-medium">14-Day Free Trial</span>
          </div>
          
          {/* Logo */}
          <div className="mb-8">
            <img src="/logo.png" alt="Reliefify" className="h-80 md:h-96 w-auto mx-auto object-contain" />
          </div>
          
          {/* Headline */}
          <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-slate-900 tracking-tight leading-[1.1] mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Relief teaching,<br className="hidden sm:block" /> made easier.
          </h1>
          
          {/* Subheadline */}
          <p className="text-lg sm:text-xl text-slate-600 leading-relaxed mb-10 max-w-2xl mx-auto">
            Reliefify helps you stay organised throughout the day. Track behaviour, record lesson notes, and generate clear handover reports for the classroom teacher — all from your phone.
          </p>
          
          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/signup">
              <Button 
                data-testid="hero-cta-signup"
                className="w-full sm:w-auto h-14 px-10 rounded-full bg-teal-600 hover:bg-teal-700 text-white font-semibold text-lg shadow-md hover:shadow-lg transition-all"
              >
                Start Free Trial
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <Link to="/login">
              <Button 
                data-testid="hero-cta-login"
                variant="outline"
                className="w-full sm:w-auto h-14 px-10 rounded-full border-2 border-slate-200 hover:border-slate-300 hover:bg-slate-50 font-semibold text-lg text-slate-700"
              >
                Login
              </Button>
            </Link>
          </div>
        </div>
        
        {/* App Preview - Live screenshot */}
        <div className="mt-16 md:mt-24 max-w-3xl mx-auto">
          <div className="relative rounded-2xl overflow-hidden shadow-2xl border border-slate-200/60">
            <img 
              src="https://customer-assets.emergentagent.com/job_4121c560-d0c7-48a6-921a-6fda55f653ba/artifacts/qp7nuyai_IMG_4544.jpeg"
              alt="Reliefify in action — managing a Year 8 class with attendance, behaviour tracking, and teacher tools"
              className="w-full h-auto"
              data-testid="hero-app-screenshot"
            />
          </div>
        </div>

        {/* Built for the classroom section */}
        <div className="mt-16 md:mt-20 max-w-3xl mx-auto text-center" data-testid="differentiator-section">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-slate-900 mb-5" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Built for the classroom, not the planning desk.
          </h2>
          <p className="text-base sm:text-lg text-slate-600 leading-relaxed">
            Most education apps focus on lesson planning, resource creation, or curriculum design. Reliefify is different. We focus on what happens between 9 and 3 — the real-time challenges of managing a classroom, tracking behaviour, keeping notes, and leaving clear handovers for the next teacher.
          </p>
        </div>
      </div>
    </section>
  );
}

function FeaturesSection() {
  const features = [
    {
      icon: ClipboardList,
      title: "Instant Behaviour Tracking",
      subtitle: "Log behaviour in seconds without disrupting your lesson.",
      description: "Quickly record positive and negative behaviour with simple taps. Keep track of patterns throughout the day and ensure nothing is forgotten when it's time to report back to the classroom teacher."
    },
    {
      icon: FileText,
      title: "Automatic Teacher Handover Reports",
      subtitle: "Create clear end-of-day reports in seconds.",
      description: "Reliefify automatically generates professional handover notes summarising the day — including behaviour, lesson progress, and key events — so the classroom teacher always knows what happened."
    },
    {
      icon: Users,
      title: "Smart Classroom Attendance",
      subtitle: "Mark attendance quickly and accurately.",
      description: "Track present, absent, and late students with one-tap controls. Optional timestamps help keep accurate records when needed."
    },
    {
      icon: Clock,
      title: "Out-of-Class Movement Tracking",
      subtitle: "Know exactly who left the room and why.",
      description: "Log toilet breaks, office visits, and other student movements so you always have a clear record if questions arise later."
    },
    {
      icon: Zap,
      title: "Live Teaching Mode",
      subtitle: "Stay organised throughout the entire day.",
      description: "Reliefify keeps everything structured by lesson period, helping you track behaviour, notes, and events without losing focus on teaching."
    },
    {
      icon: BarChart3,
      title: "Your Day at a Glance",
      subtitle: "See everything that happened in one simple view.",
      description: "Review attendance, behaviour trends, and key events from the day in one dashboard, making it easy to reflect or prepare your handover notes."
    }
  ];

  return (
    <section id="features" className="py-20 md:py-32 bg-slate-50">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Everything you need in the classroom
          </h2>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Tools designed specifically for relief and casual teachers.
          </p>
        </div>
        
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {features.map((feature, index) => (
            <div 
              key={index}
              className="bg-white rounded-2xl p-8 shadow-sm hover:shadow-md transition-shadow"
              data-testid={`feature-card-${index}`}
            >
              <div className="w-12 h-12 bg-teal-50 rounded-xl flex items-center justify-center mb-5">
                <feature.icon className="w-6 h-6 text-teal-600" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                {feature.title}
              </h3>
              <p className="text-teal-600 font-medium text-sm mb-3">
                {feature.subtitle}
              </p>
              <p className="text-slate-500 text-sm leading-relaxed">
                {feature.description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function PricingSection() {
  return (
    <section id="pricing" className="py-16 md:py-24 bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Simple, affordable pricing
          </h2>
          <p className="text-lg text-slate-600">
            Try Reliefify free for 14 days. No credit card required.
          </p>
        </div>

        <div className="max-w-md mx-auto">
          <div className="bg-white rounded-2xl border-2 border-teal-200 p-8 shadow-lg">
            <div className="text-center">
              <div className="inline-flex items-center gap-2 px-3 py-1 bg-teal-100 rounded-full text-teal-800 text-sm font-medium mb-4">
                14-Day Free Trial
              </div>
              <h3 className="text-2xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Reliefify Pro
              </h3>
              <div className="mb-6">
                <span className="text-5xl font-bold text-slate-900">$4.99</span>
                <span className="text-slate-600">/month</span>
              </div>
              <div className="bg-teal-50 rounded-lg p-4 mb-6">
                <p className="text-teal-800 font-medium">14-day free trial included</p>
              </div>
              <ul className="text-left space-y-3 mb-8">
                {[
                  "Unlimited schools & classes",
                  "Quick attendance tracking",
                  "Behaviour logging",
                  "Out-of-class tracking",
                  "AI-powered lesson summaries",
                  "Live teaching mode",
                  "Earnings tracker",
                  "Emergency work resources",
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-emerald-600 flex-shrink-0" />
                    <span className="text-slate-700">{item}</span>
                  </li>
                ))}
              </ul>
              <Link to="/signup">
                <Button 
                  data-testid="pricing-cta"
                  className="w-full h-14 rounded-full bg-teal-600 hover:bg-teal-700 text-white font-semibold text-lg"
                >
                  Start Free Trial
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function AboutSection() {
  return (
    <section id="about" className="py-20 md:py-32 bg-white">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Built by a teacher who understands the job
          </h2>
        </div>
        
        {/* Founder Story */}
        <div className="max-w-3xl mx-auto mb-16">
          <div className="space-y-6 text-slate-600 text-lg leading-relaxed">
            <p>
              Hi, I'm Matt — a teacher who created Reliefify after experiencing the challenges of relief teaching firsthand.
            </p>
            <p>
              Walking into an unfamiliar classroom, learning new routines on the fly, remembering student names, managing behaviour, keeping track of notes throughout the day, and then trying to leave clear handover notes for the classroom teacher — it's a lot to juggle.
            </p>
            <p>
              I built Reliefify to solve these problems and help relief teachers stay organised, confident, and prepared in any classroom.
            </p>
          </div>
        </div>
        
        {/* Features Grid */}
        <div className="grid md:grid-cols-2 gap-8 mb-16">
          {/* Core Classroom Tools */}
          <div className="bg-slate-50 rounded-2xl p-8">
            <h3 className="text-lg font-semibold text-slate-900 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Core Classroom Tools
            </h3>
            <ul className="space-y-3 text-slate-600">
              <li className="flex items-center gap-3">
                <div className="w-1.5 h-1.5 bg-teal-500 rounded-full"></div>
                Attendance tracking
              </li>
              <li className="flex items-center gap-3">
                <div className="w-1.5 h-1.5 bg-teal-500 rounded-full"></div>
                Behaviour logging
              </li>
              <li className="flex items-center gap-3">
                <div className="w-1.5 h-1.5 bg-teal-500 rounded-full"></div>
                Lesson notes
              </li>
              <li className="flex items-center gap-3">
                <div className="w-1.5 h-1.5 bg-teal-500 rounded-full"></div>
                Automatic teacher handover reports
              </li>
            </ul>
          </div>
          
          {/* Teacher Organisation Tools */}
          <div className="bg-slate-50 rounded-2xl p-8">
            <h3 className="text-lg font-semibold text-slate-900 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Teacher Organisation Tools
            </h3>
            <ul className="space-y-3 text-slate-600">
              <li className="flex items-center gap-3">
                <div className="w-1.5 h-1.5 bg-teal-500 rounded-full"></div>
                Track pay
              </li>
              <li className="flex items-center gap-3">
                <div className="w-1.5 h-1.5 bg-teal-500 rounded-full"></div>
                Expense tracking
              </li>
              <li className="flex items-center gap-3">
                <div className="w-1.5 h-1.5 bg-teal-500 rounded-full"></div>
                Out-of-class student logging
              </li>
              <li className="flex items-center gap-3">
                <div className="w-1.5 h-1.5 bg-teal-500 rounded-full"></div>
                Daily overview of your school day
              </li>
            </ul>
          </div>
          
          {/* Classroom Management Tools */}
          <div className="bg-slate-50 rounded-2xl p-8">
            <h3 className="text-lg font-semibold text-slate-900 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Classroom Management Tools
            </h3>
            <ul className="space-y-3 text-slate-600">
              <li className="flex items-center gap-3">
                <div className="w-1.5 h-1.5 bg-teal-500 rounded-full"></div>
                Seating plan generator
              </li>
              <li className="flex items-center gap-3">
                <div className="w-1.5 h-1.5 bg-teal-500 rounded-full"></div>
                Team generator
              </li>
              <li className="flex items-center gap-3">
                <div className="w-1.5 h-1.5 bg-teal-500 rounded-full"></div>
                Live teaching mode
              </li>
            </ul>
          </div>
          
          {/* Teacher Support Tools */}
          <div className="bg-slate-50 rounded-2xl p-8">
            <h3 className="text-lg font-semibold text-slate-900 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Teacher Support Tools
            </h3>
            <ul className="space-y-3 text-slate-600">
              <li className="flex items-center gap-3">
                <div className="w-1.5 h-1.5 bg-teal-500 rounded-full"></div>
                Emergency classroom resources
              </li>
              <li className="flex items-center gap-3">
                <div className="w-1.5 h-1.5 bg-teal-500 rounded-full"></div>
                Classroom resource store
              </li>
            </ul>
          </div>
        </div>
        
        {/* Closing Statement */}
        <div className="max-w-2xl mx-auto text-center">
          <p className="text-xl md:text-2xl text-slate-700 font-medium leading-relaxed" style={{ fontFamily: 'Manrope, sans-serif' }}>
            When you're organised, you can focus on what matters most — the students.
          </p>
        </div>
      </div>
    </section>
  );
}

function PrivacySection() {
  return (
    <section id="privacy" className="py-20 md:py-28 bg-slate-50" data-testid="privacy-section">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-10">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-emerald-100 mb-5">
            <ShieldCheck className="w-7 h-7 text-emerald-700" />
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Student Privacy First
          </h2>
        </div>

        <div className="space-y-5 text-slate-600 text-base sm:text-lg leading-relaxed">
          <p>
            Reliefify was designed specifically for casual teachers, with student privacy in mind.
          </p>
          <p>
            The app is not a student management system and does not connect to school databases. Instead, it simply helps teachers organise their day and manage classes while they are teaching.
          </p>
          <p>
            Reliefify follows a <strong className="text-slate-800">data-minimisation approach</strong>, meaning the app only uses the minimum information needed for teachers to run a class.
          </p>

          <ul className="space-y-3 my-6 pl-1">
            {[
              'Teachers typically enter shortened student names or initials (for example: Smith J.)',
              'Reliefify does not require full student records',
              'The app does not access school databases',
              'Student information is not shared with third parties',
            ].map((item, i) => (
              <li key={i} className="flex items-start gap-3">
                <ShieldCheck className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                <span>{item}</span>
              </li>
            ))}
          </ul>

          <p>
            Any names entered are simply used by the teacher for tools like seating plans, class lists, or lesson summaries during the school day.
          </p>
          <p>
            Reliefify was built to support teachers without collecting unnecessary student data.
          </p>
        </div>

        {/* Privacy Tip info box */}
        <div className="mt-10 bg-white border border-emerald-200 rounded-2xl p-6 sm:p-8" data-testid="privacy-tip-box">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center flex-shrink-0">
              <Info className="w-5 h-5 text-emerald-700" />
            </div>
            <div>
              <h3 className="text-base font-semibold text-slate-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Privacy Tip for Teachers
              </h3>
              <p className="text-sm sm:text-base text-slate-600 leading-relaxed">
                To protect student privacy, we recommend entering initials or shortened names only when creating class lists.
              </p>
              <div className="mt-3 bg-slate-50 rounded-lg px-4 py-3 inline-block">
                <p className="text-sm text-slate-500">Example:</p>
                <p className="text-sm text-slate-800 font-medium">
                  John Smith &rarr; <span className="text-emerald-700">Smith J.</span>
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-8 text-center">
          <Link to="/privacy" className="text-sm font-medium text-emerald-700 hover:text-emerald-800 transition-colors" data-testid="read-full-privacy">
            Read our full Privacy Policy &rarr;
          </Link>
        </div>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="bg-slate-900 text-white py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col md:flex-row justify-between items-center gap-6">
          <div className="flex items-center">
            <img src="/logo.png" alt="Reliefify" className="h-10 w-auto object-contain" />
          </div>
          <div className="flex gap-6 text-slate-400">
            <a href="#features" className="hover:text-white transition-colors">Features</a>
            <a href="#pricing" className="hover:text-white transition-colors">Pricing</a>
            <a href="#about" className="hover:text-white transition-colors">About</a>
            <Link to="/privacy" className="hover:text-white transition-colors">Privacy</Link>
          </div>
          <p className="text-slate-400 text-sm">
            © {new Date().getFullYear()} Reliefify. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
}

export default function MarketingHomePage() {
  const { user } = useAuth();
  
  // Scroll to section if hash in URL
  useEffect(() => {
    const hash = window.location.hash;
    if (hash) {
      const element = document.querySelector(hash);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  }, []);

  return (
    <div className="min-h-screen">
      <MarketingNav />
      <HeroSection />
      <FeaturesSection />
      <PricingSection />
      <AboutSection />
      <PrivacySection />
      <Footer />
    </div>
  );
}
